<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useDark } from '@vueuse/core'

import Badge from 'primevue/badge'
import { ChevronDown } from 'lucide-vue-next'
import type { Component } from 'vue'

interface Props {
  icon: Component
  isExpanded: boolean
  label: string
  route?: string
  isActive?: boolean
  badgeValue?: number
  badgeColor?: string
  disabled?: boolean
  disabledTooltip?: string
  children?: { title: string; path: string; icon?: Component }[]
}

const props = withDefaults(defineProps<Props>(), {
  badgeValue: 0,
  badgeColor: 'success',
  disabled: false,
})

const emit = defineEmits<{
  click: []
  openFloatingMenu: [
    event: MouseEvent,
    children: { title: string; path: string; icon?: Component }[],
  ]
}>()

const currentRoute = useRoute()
const isDark = useDark()
const isSubmenuOpen = ref(false)

const showBadge = computed(() => props.badgeValue > 0)
const displayBadge = computed(() => (props.badgeValue > 99 ? '99+' : props.badgeValue))

// `badgeColor` acepta una severidad de PrimeVue ('danger', 'warn'...) o un color
// CSS. Lo segundo es para los contadores que tienen que coincidir con un color
// de la app —la insignia de WhatsApp usa el naranja del filtro "Por responder"—,
// que ninguna severidad del tema reproduce.
const isCustomBadgeColor = computed(() => /^(#|rgb|hsl|var\()/.test(props.badgeColor))

const badgeSeverity = computed(() => (isCustomBadgeColor.value ? undefined : props.badgeColor))

const badgeStyle = computed(() =>
  isCustomBadgeColor.value ? { backgroundColor: props.badgeColor, color: '#fff' } : undefined,
)

const handleClick = (event?: MouseEvent) => {
  if (props.disabled) return

  if (props.children) {
    if (props.isExpanded) {
      isSubmenuOpen.value = !isSubmenuOpen.value
    } else if (event) {
      emit('openFloatingMenu', event, props.children)
    }
  } else {
    emit('click')
  }
}

const isRouteActive = () => {
  const currentPath = currentRoute.path

  // Si el item tiene hijos, verificar si algún hijo está activo
  if (props.children) {
    return props.children.some((child) => currentPath === child.path)
  }

  // Si no tiene hijos, comparar directamente
  return currentPath === props.route
}

const isChildActive = (childPath: string) => {
  return currentRoute.path === childPath
}
</script>

<template>
  <li
    class="w-full"
    v-tooltip.right="disabled && disabledTooltip ? disabledTooltip : null"
  >
    <!-- Router link para navegación sin hijos -->
    <router-link
      v-if="route && !children"
      :to="disabled ? currentRoute.fullPath : route"
      :aria-disabled="disabled"
      :tabindex="disabled ? -1 : 0"
      @click="disabled ? $event.preventDefault() : undefined"
      class="group flex items-center gap-3 mb-1 p-2.5 rounded-lg transition-all duration-200 ease-in-out"
      :class="[
        { 'justify-center': !isExpanded },
        disabled
          ? 'pointer-events-none cursor-not-allowed text-white/40 opacity-60 dark:text-surface-500'
          : isRouteActive()
          ? 'bg-white/18 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_8px_22px_rgba(0,0,0,0.10)] ring-1 ring-white/30 backdrop-blur-md dark:bg-[var(--p-primary-color)]/15 dark:text-[var(--p-primary-color)] dark:ring-[var(--p-primary-color)]/30 dark:shadow-[0_0_18px_color-mix(in_srgb,var(--p-primary-color)_18%,transparent)]'
          : 'text-[var(--p-surface-50)] hover:bg-white/10 hover:text-white dark:text-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-800)] dark:hover:text-[var(--p-primary-color)]',
      ]"
    >
      <div class="relative flex-shrink-0">
        <component
          :is="icon"
          :size="20"
          class="flex-shrink-0 text-current transition-colors duration-200"
        />

        <Badge
          v-if="showBadge && !isExpanded"
          :value="displayBadge"
          :severity="badgeSeverity"
          :style="badgeStyle"
          size="small"
          class="absolute -top-2 -right-2 min-w-[18px] h-[18px] text-[10px]"
        />
      </div>

      <span v-if="isExpanded" class="text-sm font-sans font-semibold transition-all duration-200">
        {{ label }}
      </span>

      <Badge
        v-if="showBadge && isExpanded"
        :value="displayBadge"
        :severity="badgeSeverity"
        :style="badgeStyle"
        size="small"
        class="ml-auto"
      />
    </router-link>

    <hr
      v-if="route && !children"
      class="mx-auto w-8/9 border-0 border-b border-white/20 dark:border-surface-700/70"
    />

    <!-- Elemento con hijos -->
    <div v-else-if="children" class="w-full">
      <button
        @click="handleClick($event)"
        class="group flex items-center gap-3 w-full p-2.5 rounded-lg transition-all duration-200 ease-in-out"
        :class="[
          { 'justify-center': !isExpanded },
          isRouteActive()
            ? 'bg-white/18 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_8px_22px_rgba(0,0,0,0.10)] ring-1 ring-white/30 backdrop-blur-md dark:bg-[var(--p-primary-color)]/15 dark:text-[var(--p-primary-color)] dark:ring-[var(--p-primary-color)]/30 dark:shadow-[0_0_18px_color-mix(in_srgb,var(--p-primary-color)_18%,transparent)]'
            : 'text-white/80 hover:bg-white/10 hover:text-white dark:text-surface-300 dark:hover:bg-[var(--p-surface-800)] dark:hover:text-[var(--p-primary-color)]',
        ]"
      >
        <component
          :is="icon"
          :size="20"
          :stroke-width="2"
          class="flex-shrink-0 text-current transition-colors duration-200"
        />

        <span
          v-if="isExpanded"
          class="text-sm font-medium flex-1 text-left transition-all duration-200"
        >
          {{ label }}
        </span>

        <ChevronDown
          v-if="isExpanded"
          :size="16"
          class="text-current transition-all duration-200"
          :class="{ 'rotate-180': isSubmenuOpen }"
        />
      </button>

      <!-- Submenu expandido -->
      <div v-if="isExpanded" class="overflow-hidden">
        <transition
          enter-active-class="transition-all duration-200 ease-out"
          leave-active-class="transition-all duration-150 ease-in"
          enter-from-class="opacity-0 max-h-0"
          enter-to-class="opacity-100 max-h-96"
          leave-from-class="opacity-100 max-h-96"
          leave-to-class="opacity-0 max-h-0"
        >
          <ul
            v-show="isSubmenuOpen"
            class="mt-1 ml-8 space-y-1 border-l border-white/15 pl-3 dark:border-surface-700/70"
          >
            <li v-for="child in children" :key="child.path">
              <router-link
                :to="child.path"
                class="group flex items-center gap-2 rounded-md p-2 pl-3 text-xs font-medium transition-all duration-200"
                :class="
                  isChildActive(child.path)
                    ? 'bg-white/16 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_6px_18px_rgba(0,0,0,0.10)] ring-1 ring-white/25 backdrop-blur-md dark:bg-[var(--p-primary-color)]/15 dark:text-[var(--p-primary-color)] dark:ring-[var(--p-primary-color)]/25'
                    : 'text-white/70 hover:bg-white/10 hover:text-white dark:text-surface-400 dark:hover:bg-[var(--p-surface-800)] dark:hover:text-[var(--p-primary-color)]'
                "
              >
                <component
                  :is="child.icon"
                  v-if="child.icon"
                  :size="14"
                  :stroke-width="2"
                  class="flex-shrink-0 text-current transition-colors duration-200"
                />

                <div
                  v-else
                  class="h-1.5 w-1.5 rounded-full transition-colors duration-200"
                  :class="
                    isChildActive(child.path)
                      ? 'bg-[var(--p-primary-color)]'
                      : 'bg-current opacity-70 group-hover:opacity-100'
                  "
                />

                <span class="truncate">
                  {{ child.title }}
                </span>
              </router-link>
            </li>
          </ul>
        </transition>
      </div>
    </div>

    <!-- Botón genérico -->
    <button
      v-else
      @click="handleClick()"
      class="group flex items-center gap-3 w-full p-2.5 rounded-lg transition-all duration-200 ease-in-out"
      :class="[
        { 'justify-center': !isExpanded },
        'text-white/80 hover:bg-white/10 hover:text-white dark:text-surface-300 dark:hover:bg-[var(--p-surface-800)] dark:hover:text-[var(--p-primary-color)]',
      ]"
    >
      <component
        :is="icon"
        :size="20"
        :stroke-width="2"
        class="flex-shrink-0 text-current transition-colors duration-200"
      />

      <span v-if="isExpanded" class="text-sm font-medium transition-colors duration-200">
        {{ label }}
      </span>
    </button>
  </li>
</template>
